import { type BriefContent, type BriefFramingMarkers, type FollowUpRecord } from '@sigora/domain';
import type { ReactNode } from 'react';

import { BRIEF_HEADINGS } from '../brief-copy.ts';
import type { FollowUpScope } from '../follow-ups/FollowUpThread.tsx';
import { Appendix } from './Appendix.tsx';
import { EvidenceCard } from './EvidenceCard.tsx';
import { MethodBlock } from './MethodBlock.tsx';
import { RecommendationList } from './RecommendationList.tsx';
import { RevisionLines, type RevisionMarks, UpdatedTag } from './RevisionMarks.tsx';
import { SummaryList } from './SummaryList.tsx';
import { type BriefEdits, Verdict } from './Verdict.tsx';

/**
 * One version of the Brief as six blocks, in the order the Brief stores them: the Verdict, the
 * Executive summary, the Recommendation with what is not settled, the evidence by research
 * question, Method and audience, and the Appendix, collapsed.
 *
 * Every block carries the marks the version's own framing leaves (`markers`, worked out once by
 * whoever read the version), and, with `marks`, what Sigora's revision says on the blocks it
 * changed. With `edits` the framing can be edited; without, the blocks are read-only.
 *
 * `record` is what the Brief's follow-ups added to it as it stands now: each note under the card
 * it names, and the follow-up log in the Appendix. A past version is drawn without one, so it shows
 * neither.
 *
 * With `onAsk` each card offers to ask about its research question; without, none does.
 *
 * `verdictFooter` is drawn last in the Verdict's block, under what Sigora's revision says there.
 *
 * With `allThemeRows` every theme strip draws all its rows and no "+K more", for a page nobody can
 * press.
 */
export const BriefBlocks = ({
  content: brief,
  markers: framing,
  edits,
  marks,
  record,
  onAsk,
  verdictFooter,
  allThemeRows = false,
}: {
  content: BriefContent;
  markers: BriefFramingMarkers;
  edits?: BriefEdits;
  marks: RevisionMarks | null;
  record?: FollowUpRecord;
  /** Ask about this: hands a card to the Brief's conversation; absent, a card offers none. */
  onAsk?: (card: FollowUpScope) => void;
  verdictFooter?: ReactNode;
  allThemeRows?: boolean;
}) => {
  return (
    <>
      <section className="brief-block" data-brief-block="verdict">
        <Verdict verdict={brief.verdict} edited={framing.verdict} edits={edits} marks={marks} />
        <RevisionLines marks={marks} block="verdict" />
        {verdictFooter}
      </section>
      <section className="brief-block" data-brief-block="summary">
        <SummaryList summary={brief.summary} edited={framing.summary} edits={edits} marks={marks} />
        <RevisionLines marks={marks} block="summary" />
      </section>
      <section className="brief-block" data-brief-block="recommendation">
        <RecommendationList
          recommendation={brief.recommendation}
          cards={brief.evidence.cards}
          reordered={framing.recommendation}
          edits={edits}
          marks={marks}
        />
        <RevisionLines marks={marks} block="recommendation" />
      </section>
      <section className="brief-block" data-brief-block="evidence">
        <h2>
          {BRIEF_HEADINGS.evidence}
          <UpdatedTag marks={marks} block="evidence" />
        </h2>
        {brief.evidence.cards.map((card) => (
          <EvidenceCard
            key={card.rq_id}
            card={card}
            notSettled={brief.recommendation.not_settled.find(
              (entry) => entry.rq_id === card.rq_id,
            )}
            notes={record?.notes.filter((note) => note.rqId === card.rq_id)}
            onAsk={onAsk}
            allThemeRows={allThemeRows}
          />
        ))}
        <RevisionLines marks={marks} block="evidence" />
      </section>
      <section className="brief-block" data-brief-block="method">
        <MethodBlock method={brief.method} marks={marks} />
        <RevisionLines marks={marks} block="method" />
      </section>
      <section className="brief-block" data-brief-block="appendix">
        <Appendix
          appendix={brief.appendix}
          marks={marks}
          record={record}
          cards={brief.evidence.cards}
        />
        <RevisionLines marks={marks} block="appendix" />
      </section>
    </>
  );
};
